import { isPlatformBrowser, Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import { ResponseCreateDeal } from 'src/app/core/data/deal/deal.model';
import { DealProvider } from 'src/app/core/data/deal/deal.provider';
import { Event } from 'src/app/core/data/event/event.model';
import { EventProvider } from 'src/app/core/data/event/event.provider';
import { Product } from 'src/app/core/data/product/product.model';
import { SearchService } from '../../search/service/search.service';
import { phoneBrValidator } from 'src/app/core/validators/phone-br.validator';
import { environment, region_phrases, RegionNames } from 'src/environments/environment';
import { SeoService } from 'src/app/core/services/seo.service';

declare var $: any;

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  // public event$: Observable<Event>;
  public event: Event;
  public product?: Product;
  public previousProduct?: Product;
  public nextProduct?: Product;

  public modalDealShow = false;
  public showGallery = false;
  public galleryImg: string;
  public processing = false;
  public dealRes?: ResponseCreateDeal;

  public modalDealSuccessShow = false;

  // Gallery navigation
  public galleryItems: Array<{ type: 'video' | 'image', url: string, embedUrl?: any }> = [];
  public currentGalleryIndex = 0;

  public translations = region_phrases[environment.region_id as keyof RegionNames];
  public environment = environment;

  public form: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    phoneNumber: new UntypedFormControl('', [Validators.required, phoneBrValidator]),
    attendantName: new UntypedFormControl(''),
    message: new UntypedFormControl(''),
  });

  private paramsSubscribe: Subscription;

  // lógica acrescentada para voltar para a página de busca
  private cameFromSearch = false;
  
  // Rastrear se houve navegação interna
  private hasInternalNavigation = false;


  private isBrowser: boolean;

  constructor(
    private readonly location: Location,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly eventData: EventProvider,
    private readonly dealProvider: DealProvider,
    private searchService: SearchService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    // Rastrear se houve navegação anterior no Angular
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.hasInternalNavigation = true;
    });
  }

  ngOnInit(): void {
    this.paramsSubscribe = this.route.params.subscribe((params) => {
      this.product = undefined;
      this.previousProduct = undefined;
      this.nextProduct = undefined;
      this.eventData.info(params.eventId).pipe(
        tap((event: Event) => {
          const productId = parseInt(params.id);
          this.product = event.lots.find((x) => x.id === productId)!;

          const currentIndex = event.lots.findIndex((x) => x.id === productId);
          if (currentIndex > 0) {
            this.previousProduct = event.lots[currentIndex - 1];
          }
          if (currentIndex < event.lots.length - 1) {
            this.nextProduct = event.lots[currentIndex + 1];
          }

          if(environment.region_id == "bo")
          {
            this.product.paymentConditions = this.product.paymentConditions.replace('R$', 'BOB');
            this.product.paymentConditionObservation = this.product.paymentConditionObservation.replace('R$', 'BOB');
          }
          this.event = event;
          for (const field of event.dealExtraFields) {
            this.form.addControl(field.name, new UntypedFormControl('', field.required ? [Validators.required] : []))
          }
          // Atualizar o título e as meta tags
          this.seoService.setTitle(this.product.title + ' - Remate Agroshop');
          this.seoService.setDescription(this.product.description);
          this.seoService.setOgDescription(this.product.description);
          this.seoService.setOgTitle(this.product.title + ' - Remate Agroshop');
          this.seoService.setOgUrl();
          //caso exista imagens em this.product.lotImages, setar a primeira como imagem da tag og:image
          if (this.product.lotImages.length > 0) {
            this.seoService.setOgImage(this.product.lotImages[0].imageUrl);
          }

          // Inicializar galeria com vídeos e imagens combinados
          this.initGalleryItems();
        }),
        tap(() => {
          if (this.isBrowser) {
            setTimeout(() => this.initSlider(), 10);
          }
        }),
      ).subscribe();
    });
    this.checkNavigationOrigin();
    this.addKeyboardListener();
  }

  ngOnDestroy(): void {
    this.paramsSubscribe.unsubscribe();
    this.removeKeyboardListener();
  }

  clearSlider(): void {
    // $('#lightSlider').lightSlider().destroy();
    // $('.lSGallery').remove();
  }

  initSlider(): void {
    this.clearSlider();

    $('#lightSlider').lightSlider({
      gallery: true,
      item: 1,
      thumbItem: 6,
      slideMargin: 0,
      speed: 500,
      auto: false,
      loop: true,
      onBeforeSlide: () => this.pauseVideo()
    });
  }

  onClickNegociar(): void {
    this.dealRes = undefined;
    this.modalDealShow = true;
    this.modalDealSuccessShow = false;
    setTimeout(() => {
      this.form.reset();
    }, 200);
  }

  onClickConfirmNegociar(): void {
    if (!this.product) return;
    this.processing = true;
    this.dealProvider
      .create({
        ...this.form.value,
        extraFields: this.event.dealExtraFields.reduce((obj, f) => { obj[f.name] = this.form.controls[f.name].value; return obj }, {} as Record<string, string>),
        lotId: this.product.id,
        dealType: this.product.saleType,
        value: this.product.saleType === 'shopping' ? this.product.unitValue : this.product.nextBid,
      })
      .subscribe((res) => {
        this.dealRes = res;
        this.processing = false;
        if (res.success === true) {
          this.modalDealShow = false;
          this.modalDealSuccessShow = true;
        }
      });
  }

  fieldInvalid(name: string): boolean {
    const field = this.form.get(name)!;
    return field.invalid && (field.dirty || field.touched);
  }

  pauseVideo() {
    if (!this.isBrowser) return;
    $('.yt-product-video').each(function () {
      $(this).attr("src", $(this).attr("src"));
    });
  }

  goBack() {
    //console.log('🔍 DEBUG goBack() chamado');
    //console.log('hasInternalNavigation:', this.hasInternalNavigation);
    //console.log('cameFromSearch:', this.cameFromSearch);
    //console.log('document.referrer:', document.referrer);
    
    // Prioridade 1: Se veio da busca, volta para a página de busca
    if (this.cameFromSearch) {
      //console.log('✅ Redirecionando para /busca');
      this.router.navigate(['/busca']);
      return;
    }

    // Prioridade 2: Se houve navegação interna do Angular, usar location.back()
    if (this.hasInternalNavigation) {
      //console.log('✅ Usando location.back() - navegação interna detectada');
      this.location.back();
      return;
    }

    // Prioridade 3: Verificar se o referrer é do nosso próprio site (para primeira navegação)
    if (!this.isBrowser) {
      this.router.navigate(['/']);
      return;
    }
    const referrer = document.referrer;
    const currentOrigin = window.location.origin;

    if (referrer && referrer.startsWith(currentOrigin)) {
      //console.log('✅ Usando location.back() - referrer válido do site');
      this.location.back();
      return;
    }

    // Prioridade 4: Se não há navegação válida, vai para a home
    //console.log('✅ Redirecionando para home / - acesso direto');
    this.router.navigate(['/']);
  }

  openGallery(index: number) {
    this.currentGalleryIndex = index;
    this.updateCurrentGalleryItem();
    this.showGallery = true;
  }

  initGalleryItems() {
    if (!this.product) return;

    this.galleryItems = [];

    // Adicionar vídeos
    this.product.lotVideos.forEach(video => {
      this.galleryItems.push({
        type: 'video',
        url: video.thumbnail,
        embedUrl: video.embedUrl
      });
    });

    // Adicionar imagens
    this.product.lotImages.forEach(image => {
      this.galleryItems.push({
        type: 'image',
        url: image.imageLotUrl
      });
    });
  }

  updateCurrentGalleryItem() {
    if (this.galleryItems.length > 0 && this.currentGalleryIndex >= 0 && this.currentGalleryIndex < this.galleryItems.length) {
      const currentItem = this.galleryItems[this.currentGalleryIndex];
      this.galleryImg = currentItem.url;
    }
  }

  nextGalleryItem() {
    if (this.currentGalleryIndex < this.galleryItems.length - 1) {
      this.currentGalleryIndex++;
      this.updateCurrentGalleryItem();
    }
  }

  previousGalleryItem() {
    if (this.currentGalleryIndex > 0) {
      this.currentGalleryIndex--;
      this.updateCurrentGalleryItem();
    }
  }

  getCurrentGalleryItem() {
    return this.galleryItems[this.currentGalleryIndex];
  }

  private handleKeyboardEvent = (event: KeyboardEvent) => {
    if (!this.showGallery) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previousGalleryItem();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.nextGalleryItem();
    }
  }

  private addKeyboardListener() {
    if (this.isBrowser) {
      window.addEventListener('keydown', this.handleKeyboardEvent);
    }
  }

  private removeKeyboardListener() {
    if (this.isBrowser) {
      window.removeEventListener('keydown', this.handleKeyboardEvent);
    }
  }

  // lógica acrescentada para voltar para a página de busca
  private checkNavigationOrigin() {
    this.route.queryParams.subscribe(params => {
      this.cameFromSearch = params['from'] === 'busca';
    });
  }
}
